import {
  Col,
  Row,
  Form,
  Modal,
  Button,
  Spinner,
  Container
} from "react-bootstrap";
import ReactQuill from "react-quill";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import {
  SEO_PAGE_IMAGE_SECTIONS,
  SUB_PAGE_SYSTEM_TAG_IDENTIFIER
} from "../../../strings";
import {
  parseResourceSrc,
  replaceAllUnderscore,
  parsePreviewResourceSrc
} from "../../../../services/common-service";
import TextArea from "../../../reusable-form-fields/text-area";
import PreventReload from "../../../funnel-form/prevent-reload";
import TextField from "../../../reusable-form-fields/text-field";
import FileInput from "../../../reusable-form-fields/input-file";
import { updateLocalSeo } from "../../../../services/api-declaration";
import { showError, showSuccess } from "../../../../services/toast-service";
import ValidationMessage from "../../../validation-message/validation-message";
import { EditSubPageSchema } from "../../website-schema/local-seo-validation-schema";
import { useTranslation } from "react-i18next";

const EditWebsiteSubPageDialog = ({
  show,
  data,
  toggleDialog,
  onAfterEditSuccess
}) => {
  const { t } = useTranslation();
  // Initialize form with nested data
  const methods = useForm({
    resolver: yupResolver(EditSubPageSchema),
    defaultValues: data
  });
  const {
    watch,
    setValue,
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting }
  } = methods;

  const onSaveChanges = async (formValues) => {
    try {
      const payload = { seo: formValues.seo, sections: formValues.sections };
      const response = await updateLocalSeo(data.seoPageId, payload);
      onAfterEditSuccess(payload); // parent method for update grid state
      showSuccess(response.data?.message || "Local Seo updated successfully!");
      toggleDialog(); // close dialog after submit
    } catch (error) {
      console.log("Error in update: ", error);
      showError(error.response?.data?.nessage || "Error in update!");
    }
  };
  const handleQuillChange = ({ value, index }) => {
    const quillString =
      value === "<p><br></p>" || value === "<p> </p>" ? null : value;
    setValue(`sections.${index}.data.content.paragraph`, quillString, {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  return (
    <Modal
      size="lg"
      show={show}
      backdrop="static"
      onHide={toggleDialog}
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      <Modal.Body>
        <FormProvider {...methods}>
          <PreventReload />
          <Form>
            <Container className="px-0">
              {/* Disabled Field: Keyword + City */}
              <Row className="mb-4">
                <Col sm={12} md={6}>
                  <TextField
                    name="combined"
                    textFieldClasses="text-capitalize"
                    label={t("localSeo.editPageTitle")}
                    defaultValue={`${data.keywordName} - ${data.city}`}
                    disabled
                    boldLabel
                  />
                </Col>
              </Row>

              {/* SEO Section */}
              <Row>
                <Col xs={12}>
                  <TextField
                    register={register}
                    name="seo.title"
                    label={t("localSeo.seoTitle")}
                    errors={errors?.seo?.title}
                  />
                </Col>
                <Col xs={12}>
                  <TextArea
                    register={register}
                    name="seo.description"
                    label={t("localSeo.seoDescription")}
                    errors={errors?.seo?.description}
                  />
                </Col>
              </Row>

              {/* Dynamic Sections */}
              <h5 className="mt-4">{t("localSeo.sections")}</h5>
              {data.sections?.map((section, idx) => (
                <div key={idx} className="border rounded p-3 mb-3">
                  <h5 className="mb-3 text-capitalize">
                    {replaceAllUnderscore(section.name)}
                  </h5>
                  {/* Heading */}
                  <TextField
                    register={register}
                    label={t("localSeo.heading")}
                    name={`sections.${idx}.data.content.heading`}
                    errors={errors?.sections?.[idx]?.data?.content?.heading}
                  />
                  {/* Paragraph */}
                  <Form.Group controlId="contentParagraph">
                    <Form.Label
                      className={`mb-2 ${
                        errors?.sections?.[idx]?.data?.content?.paragraph &&
                        "text-danger"
                      }`}
                    >
                      {`${t("localSeo.paragraph")} ${
                        errors?.sections?.[idx]?.data?.content?.paragraph
                          ? "*"
                          : ""
                      }`}
                    </Form.Label>
                    <ReactQuill
                      theme="snow"
                      value={watch(`sections.${idx}.data.content.paragraph`)}
                      onChange={(value) => {
                        handleQuillChange({ value, index: idx });
                      }}
                      preserveWhitespace
                    />
                    {errors?.sections?.[idx]?.data?.content?.paragraph && (
                      <ValidationMessage
                        error={
                          errors.sections[idx].data.content.paragraph?.message
                        }
                      />
                    )}
                  </Form.Group>
                  {/* Images */}
                  {(section.data?.images?.length > 0 ||
                    SEO_PAGE_IMAGE_SECTIONS.includes(section.name)) && (
                    <Row className="mt-3">
                      {(section.data?.images?.length > 0
                        ? section.data.images
                        : // render one empty image field
                          [null]
                      )?.map((_, imgIdx) => (
                        <Col xs={12} md={8} key={imgIdx} className="mb-2">
                          <FileInput
                            label={t("customerWebsite.image")}
                            name={`sections[${idx}].data.images[${imgIdx}]`}
                            parentFolderName={"customer"}
                            url={parseResourceSrc(
                              watch(`sections[${idx}].data.images[${imgIdx}]`)
                            )}
                            urlForPreview={parsePreviewResourceSrc(
                              watch(`sections[${idx}].data.images[${imgIdx}]`)
                            )}
                            asset={
                              watch(`sections[${idx}].data.images[${imgIdx}]`)
                                ?.asset
                            }
                            tagName={
                              SUB_PAGE_SYSTEM_TAG_IDENTIFIER[section.name]
                            }
                          />
                        </Col>
                      ))}
                    </Row>
                  )}
                </div>
              ))}
            </Container>
          </Form>
        </FormProvider>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          disabled={isSubmitting}
          onClick={() => toggleDialog()}
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant="primary"
          disabled={isSubmitting || !isDirty}
          onClick={handleSubmit(onSaveChanges)}
        >
          {isSubmitting && (
            <Spinner size="sm" className="me-2" animation="border" />
          )}
          {t("common.save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditWebsiteSubPageDialog;
